
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { CalendarEvent } from '../types';
import { differenceInHours, addDays, isBefore, parseISO } from 'date-fns';
import { fromLocalISOString, toLocalISOString } from '@/utils/timezoneUtils';

interface ShiftTemplate {
  id: string;
  team_id: string;
  name: string;
  description: string | null;
  schedule_data: {
    shifts: (Omit<CalendarEvent, 'assigned_guards' | 'guard_names'> & { shift_duration_hours?: number })[];
  };
  template_type: 'week' | 'custom';
  created_at: string;
  created_by: string;
}

interface SaveTemplateParams {
  teamId: string;
  name: string;
  description?: string;
  shifts: CalendarEvent[];
  templateType?: 'week' | 'custom';
}

export const useShiftTemplates = (teamId?: string) => {
  const [templates, setTemplates] = useState<ShiftTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  const fetchTemplates = async () => {
    if (!teamId) return;

    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('shift_templates')
        .select('*')
        .eq('team_id', teamId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform the data to ensure it matches our ShiftTemplate type
      const typedTemplates: ShiftTemplate[] = (data || []).map(item => ({
        ...item,
        schedule_data: typeof item.schedule_data === 'string' 
          ? JSON.parse(item.schedule_data) 
          : item.schedule_data || { shifts: [] },
        // Ensure template_type is either 'week' or 'custom'
        template_type: (item.template_type === 'week' || item.template_type === 'custom') 
          ? item.template_type 
          : 'week' // Default to 'week' if the value is not valid
      }));
      
      setTemplates(typedTemplates);
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError(err.message || t('errorFetchingTemplates') || 'Error fetching templates');
      toast.error(t('errorFetchingTemplates') || 'Error fetching templates');
    } finally {
      setIsLoading(false);
    }
  };

  const saveTemplate = async ({ teamId, name, description, shifts, templateType = 'week' }: SaveTemplateParams) => {
    if (!teamId || !shifts.length) {
      toast.error(t('noShiftsToSave') || 'No shifts to save as template');
      return null;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Clean the shifts data and fix overnight shift relationships
      const cleanedShifts = shifts.map(shift => {
        let startDate = fromLocalISOString(shift.start_date);
        let endDate = fromLocalISOString(shift.end_date);
        
        // Fix overnight shifts - if end time is before start time on the same day, move end to next day
        if (endDate <= startDate) {
          endDate = addDays(endDate, 1);
        }
        
        // Calculate the duration to preserve overnight shifts
        const durationHours = differenceInHours(endDate, startDate);
        
        // Ensure duration is positive
        const finalDuration = durationHours <= 0 ? 24 + durationHours : durationHours;
        
        const cleanedShift = {
          title: shift.title,
          description: shift.description || '',
          start_date: toLocalISOString(startDate),
          end_date: toLocalISOString(endDate),
          location: shift.location || '',
          color: shift.color || 'Primary',
          team_id: teamId,
          id: crypto.randomUUID(),
          // Store duration info to help with overnight shifts
          shift_duration_hours: finalDuration
        };
        
        return cleanedShift;
      });
      
      const scheduleData = {
        shifts: cleanedShifts
      };
      
      // Ensure templateType is valid
      const validTemplateType: 'week' | 'custom' = 
        templateType === 'week' || templateType === 'custom' ? templateType : 'week';
      
      const { data, error } = await supabase
        .from('shift_templates')
        .insert({
          team_id: teamId,
          name,
          description,
          schedule_data: scheduleData,
          template_type: validTemplateType,
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      
      toast.success(
        t('templateSaved') || 'Template saved', 
        { description: t('templateSavedDescription') || `${name} has been saved with ${cleanedShifts.length} shifts` }
      );
      
      // Refresh templates
      await fetchTemplates();
      
      return data;
    } catch (err: any) {
      console.error('Error saving template:', err);
      setError(err.message || t('errorSavingTemplate') || 'Error saving template');
      toast.error(t('errorSavingTemplate') || 'Error saving template');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTemplate = async (templateId: string) => {
    if (!templateId) return false;

    try {
      const { error } = await supabase
        .from('shift_templates')
        .delete()
        .eq('id', templateId);
      
      if (error) throw error;
      
      // Update local state
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      
      toast.success(t('templateDeleted') || 'Template deleted');
      return true;
    } catch (err: any) {
      console.error('Error deleting template:', err);
      toast.error(t('errorDeletingTemplate') || 'Error deleting template');
      return false;
    }
  };

  useEffect(() => {
    if (teamId) {
      fetchTemplates();
    }
  }, [teamId]);

  return {
    templates,
    isLoading,
    error,
    fetchTemplates,
    saveTemplate,
    deleteTemplate
  };
};
